/**
 * ActivityPage (/activity) — the full-page version of the bell dropdown, used
 * as the mobile "Activity" tab (Sep 2026). Grouped by tool; unseen items keep
 * the plum dot, and leaving the page marks everything seen (clears the badge).
 */
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Icon } from '../design/Icon';
import { PageHeader } from '../components/PageHeader';
import { useSharedTodos, useTodosLoading } from '../notifications/TodosContext';
import { Loading } from '../components/Loading';
import { markSeen, todoSeenKey, useSeenSet } from '../notifications/seen';
import { pageFrame } from '../design/ui';

export function ActivityPage() {
  const todos = useSharedTodos();
  const loading = useTodosLoading();
  const seen = useSeenSet();
  // Mark on unmount so the dots still show what is new while reading.
  const latest = useRef(todos);
  latest.current = todos;
  useEffect(() => () => markSeen(latest.current.map((t) => todoSeenKey(t))), []);

  const byTool = new Map<string, typeof todos>();
  for (const t of todos) byTool.set(t.tool, [...(byTool.get(t.tool) ?? []), t]);

  return (
    <main className={`${pageFrame} space-y-6`}>
      <PageHeader title="Activity" summary="Everything waiting on you, across every tool." />
      {loading && todos.length === 0 ? (
        <Loading label="your to-dos" />
      ) : todos.length === 0 ? (
        <p className="rounded-lg border border-line bg-surface px-4 py-10 text-center text-sm text-fg-muted">
          You&rsquo;re all caught up.
        </p>
      ) : (
        [...byTool.entries()].map(([tool, items]) => (
          <section key={tool} className="space-y-2">
            <h2 className="text-2xs font-semibold uppercase text-fg-faint">{tool} · {items.length}</h2>
            <ul className="rounded-lg border border-line bg-surface divide-y divide-line overflow-hidden">
              {items.map((t) => (
                <li key={t.id}>
                  <Link to={t.link} className="flex items-center gap-3 px-4 py-3 hover:bg-surface-2 transition-colors duration-fast">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${seen.has(todoSeenKey(t)) ? 'bg-line-strong' : 'bg-brand-solid'}`} />
                    <span className="min-w-0 flex-1 text-sm text-fg">{t.label}</span>
                    <Icon as={ChevronRight} className="text-fg-faint" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
    </main>
  );
}
